import React, { useState, useEffect } from 'react';
import { Card, Form, InputNumber, Button, Space, message, Table, Modal, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import dayjs from 'dayjs';
import { useCurrency } from '../contexts/CurrencyContext';

interface SimplePriceManagerProps {
  productId?: number;
  isWholesalePrice?: boolean;
  isPurchasePrice?: boolean;
}

interface PriceData {
  id?: number;
  priceTypeId: number;
  priceTypeName: string;
  price: number;
}

const SimplePriceManager: React.FC<SimplePriceManagerProps> = ({ 
  productId, 
  isWholesalePrice = false,
  isPurchasePrice = false
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState<PriceData[]>([]);
  const [historyModal, setHistoryModal] = useState(false);
  const [priceHistory, setPriceHistory] = useState<any[]>([]);
  const [selectedPriceType, setSelectedPriceType] = useState<string>('');
  const { baseCurrencySymbol } = useCurrency();

  useEffect(() => {
    if (productId) {
      loadPrices();
    }
  }, [productId]);

  const loadPrices = async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      // Genel ürün fiyatlarını getir (ProductVariantId = null)
      const { data } = await api.get(`/productprices/product/${productId}`);
      
      // Son fiyatları map'le
      const priceMap = new Map();
      data.forEach((p: any) => {
        if (!p.productVariantId) { // Sadece genel fiyatlar
          if (!priceMap.has(p.priceTypeId) || 
              new Date(p.validFrom) > new Date(priceMap.get(p.priceTypeId).validFrom)) {
            priceMap.set(p.priceTypeId, p);
          }
        }
      });
      
      setPrices(Array.from(priceMap.values()).map(p => ({
        id: p.id,
        priceTypeId: p.priceTypeId,
        priceTypeName: p.priceTypeName,
        price: p.price
      })));
    } catch (error) {
      console.error('Fiyatlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrice = async (values: any) => {
    if (!productId) {
      message.error('Önce ürünü kaydedin');
      return;
    }

    try {
      setLoading(true);
      
      const requests = [];
      
      if (values.retailPrice) {
        requests.push(api.post('/productprices', {
          productId,
          priceTypeId: 1, // Perakende
          price: values.retailPrice,
          validFrom: new Date().toISOString(),
          isIndefinite: true
        }));
      }
      
      if (values.wholesalePrice && isWholesalePrice) {
        requests.push(api.post('/productprices', {
          productId,
          priceTypeId: 2, // Toptan
          price: values.wholesalePrice,
          validFrom: new Date().toISOString(),
          isIndefinite: true
        }));
      }
      
      if (values.purchasePrice && isPurchasePrice) {
        requests.push(api.post('/productprices', {
          productId,
          priceTypeId: 3, // Alış
          price: values.purchasePrice,
          validFrom: new Date().toISOString(),
          isIndefinite: true
        }));
      }

      await Promise.all(requests);
      message.success('Fiyatlar eklendi');
      form.resetFields();
      loadPrices();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fiyat eklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleShowHistory = async (priceTypeId: number, priceTypeName: string) => {
    if (!productId) return;
    
    try {
      setLoading(true);
      const { data } = await api.get(`/productprices/product/${productId}/history`);
      
      const filtered = data.filter((p: any) => 
        p.priceTypeId === priceTypeId && !p.productVariantId
      );
      
      setPriceHistory(filtered);
      setSelectedPriceType(priceTypeName);
      setHistoryModal(true);
    } catch (error) {
      message.error('Fiyat geçmişi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Fiyat Tipi',
      dataIndex: 'priceTypeName',
      key: 'priceTypeName',
    },
    {
      title: 'Fiyat',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${baseCurrencySymbol}${price.toFixed(2)}`
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: PriceData) => (
        <Space>
          <Button 
            size="small" 
            icon={<HistoryOutlined />}
            onClick={() => handleShowHistory(record.priceTypeId, record.priceTypeName)}
          >
            Geçmiş
          </Button>
        </Space>
      )
    }
  ];

  if (!productId) {
    return (
      <Card title="💰 Fiyat Yönetimi">
        <p>Fiyat eklemek için önce ürünü kaydetmelisiniz.</p>
      </Card>
    );
  }

  const getPrice = (priceTypeId: number) => {
    return prices.find(p => p.priceTypeId === priceTypeId);
  };

  const retailPrice = getPrice(1);
  const wholesalePrice = getPrice(2);
  const purchasePrice = getPrice(3);

  return (
    <>
      <Card title="💰 Fiyat Yönetimi" loading={loading} style={{ marginBottom: 16 }}>
        {/* Mevcut Fiyatlar - Inline Gösterim */}
        {prices.length > 0 && (
          <div style={{ marginBottom: 16, padding: '12px', background: '#f5f5f5', borderRadius: '6px' }}>
            <Space size="large" wrap>
              {retailPrice && (
                <div>
                  <span style={{ color: '#666', fontSize: '12px' }}>Satış: </span>
                  <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#1890ff' }}>
                    {baseCurrencySymbol}{retailPrice.price.toFixed(2)}
                  </span>
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<HistoryOutlined />}
                    onClick={() => handleShowHistory(1, 'Satış Fiyatı')}
                  />
                </div>
              )}
              
              {wholesalePrice && isWholesalePrice && (
                <div>
                  <span style={{ color: '#666', fontSize: '12px' }}>Toptan: </span>
                  <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#52c41a' }}>
                    {baseCurrencySymbol}{wholesalePrice.price.toFixed(2)}
                  </span>
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<HistoryOutlined />}
                    onClick={() => handleShowHistory(2, 'Toptan Fiyatı')}
                  />
                </div>
              )}
              
              {purchasePrice && isPurchasePrice && (
                <div>
                  <span style={{ color: '#666', fontSize: '12px' }}>Alış: </span>
                  <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#ff7875' }}>
                    {baseCurrencySymbol}{purchasePrice.price.toFixed(2)}
                  </span>
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<HistoryOutlined />}
                    onClick={() => handleShowHistory(3, 'Alış Fiyatı')}
                  />
                </div>
              )}
            </Space>
          </div>
        )}

        {/* Yeni Fiyat Ekleme Formu */}
        <div onClick={(e) => e.stopPropagation()}>
          <Form 
            form={form} 
            layout="inline" 
            onFinish={(e) => {
              e?.preventDefault?.();
              handleAddPrice(form.getFieldsValue());
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}
          >
            <Form.Item name="retailPrice" label="Satış Fiyatı" style={{ marginBottom: 0 }}>
              <InputNumber min={0} precision={2} prefix={baseCurrencySymbol} placeholder="0.00" style={{ width: 140 }} />
            </Form.Item>
            
            {isWholesalePrice && (
              <Form.Item name="wholesalePrice" label="Toptan Fiyatı" style={{ marginBottom: 0 }}>
                <InputNumber min={0} precision={2} prefix={baseCurrencySymbol} placeholder="0.00" style={{ width: 140 }} />
              </Form.Item>
            )}
            
            {isPurchasePrice && (
              <Form.Item name="purchasePrice" label="Alış Fiyatı" style={{ marginBottom: 0 }}>
                <InputNumber min={0} precision={2} prefix={baseCurrencySymbol} placeholder="0.00" style={{ width: 140 }} />
              </Form.Item>
            )}
            
            <Form.Item style={{ marginBottom: 0 }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                loading={loading}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.validateFields().then((values) => {
                    handleAddPrice(values);
                  }).catch((info) => {
                    console.log('Validate Failed:', info);
                  });
                }}
              >
                Ekle
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Card>

      {/* Fiyat Geçmişi Modal */}
      <Modal
        title={`Fiyat Geçmişi - ${selectedPriceType}`}
        open={historyModal}
        onCancel={() => setHistoryModal(false)}
        footer={null}
        width={700}
      >
        <Table
          dataSource={priceHistory}
          rowKey="id"
          pagination={false}
          size="small"
          columns={[
            {
              title: 'Fiyat',
              dataIndex: 'price',
              key: 'price',
      render: (price: number) => `${baseCurrencySymbol}${price.toFixed(2)}`
            },
            {
              title: 'Geçerlilik Başlangıç',
              dataIndex: 'validFrom',
              key: 'validFrom',
              render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm')
            },
            {
              title: 'Oluşturulma',
              dataIndex: 'createdAt',
              key: 'createdAt',
              render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm')
            }
          ]}
        />
      </Modal>
    </>
  );
};

export default SimplePriceManager;

